const P = (x, mt) => ({ t: "p", x, mt });
const H = (x) => ({ t: "h3", x });
const UL = (items) => ({ t: "ul", items });
const BOX = (items) => ({ t: "box", items });

const cookiePolicy = {
  en: {
    cookiePage: {
      back: "Back to Home",
      title: "Cookie Policy",
      lastUpdated: "Last Updated: January 18, 2025",
      links: { privacy: "Privacy Policy", terms: "Terms of Service", refund: "Refund Policy" },
      sections: [
        { h: "1. What Are Cookies?", blocks: [
          P("Cookies are small text files stored on your device when you visit websites. They help websites remember your preferences and improve your experience."),
        ] },
        { h: "2. How We Use Cookies", blocks: [
          P("EReMS Cameroon uses cookies for the following purposes:"),
          H("2.1 Essential Cookies (Required)"),
          P("These cookies are necessary for the platform to function:"),
          UL([
            "**Authentication:** Keep you logged in securely",
            "**Security:** Protect against unauthorized access",
            "**Session Management:** Remember your actions during a session",
            "**Load Balancing:** Distribute traffic across servers",
          ]),
          H("2.2 Functional Cookies"),
          P("These cookies enhance functionality:"),
          UL([
            "**Language Preferences:** Remember your language choice",
            "**Theme Settings:** Remember dark/light mode preference",
            "**Form Data:** Save form inputs temporarily",
            "**User Preferences:** Remember custom settings",
          ]),
          H("2.3 Analytics Cookies"),
          P("These cookies help us understand usage:"),
          UL([
            "**Usage Statistics:** Pages viewed, features used",
            "**Performance:** Load times, error tracking",
            "**User Journey:** Navigation patterns",
            "**Device Information:** Browser, OS, screen size",
          ]),
        ] },
        { h: "3. Types of Cookies We Use", blocks: [
          H("3.1 First-Party Cookies"),
          P("Set directly by EReMS Cameroon:"),
          BOX([
            "**erems_token:** Authentication token (Essential)",
            "**erems_lang:** Language preference (Functional)",
            "**erems_theme:** Theme preference (Functional)",
            "**erems_session:** Session identifier (Essential)",
          ]),
          H("3.2 Third-Party Cookies"),
          P("Set by external services we use:"),
          UL([
            "**Stripe:** Payment processing (stripe.com)",
            "**Analytics:** Usage tracking (if enabled)",
          ]),
        ] },
        { h: "4. Cookie Duration", blocks: [
          H("Session Cookies"),
          P("Deleted when you close your browser:"),
          UL(["Session identifier", "Temporary form data"]),
          H("Persistent Cookies"),
          P("Remain on your device for a set period:"),
          UL([
            "**Authentication:** 30 days (or until logout)",
            "**Preferences:** 1 year",
            "**Analytics:** 2 years",
          ]),
        ] },
        { h: "5. Managing Cookies", blocks: [
          H("5.1 Browser Settings"),
          P("You can control cookies through your browser settings:"),
          UL([
            "**Chrome:** Settings → Privacy and Security → Cookies",
            "**Firefox:** Settings → Privacy & Security → Cookies",
            "**Safari:** Preferences → Privacy → Cookies",
            "**Edge:** Settings → Privacy → Cookies",
          ]),
          H("5.2 Cookie Preferences"),
          P("You can manage your cookie preferences in your account settings. Note that blocking essential cookies will prevent the platform from functioning properly."),
          H("5.3 Do Not Track"),
          P("Some browsers have \"Do Not Track\" features. We respect these settings for non-essential cookies."),
        ] },
        { h: "6. Consequences of Blocking Cookies", blocks: [
          P("If you block cookies, some features may not work:"),
          UL([
            "You will need to log in on every visit",
            "Your preferences won't be saved",
            "Some features may be unavailable",
            "The site may not function optimally",
          ]),
        ] },
        { h: "7. Updates to Cookie Policy", blocks: [
          P("We may update this Cookie Policy from time to time. We will notify you of significant changes and update the \"Last Updated\" date."),
        ] },
        { h: "8. Contact Us", blocks: [
          P("Questions about cookies? Contact us:"),
          BOX(["**Operator:** M-TECH", "**Email:** mboatechnologyllc@gmail.com"]),
        ] },
      ],
    },
  },
  fr: {
    cookiePage: {
      back: "Retour à l'accueil",
      title: "Politique relative aux cookies",
      lastUpdated: "Dernière mise à jour : 18 janvier 2025",
      links: { privacy: "Politique de confidentialité", terms: "Conditions d'utilisation", refund: "Politique de remboursement" },
      sections: [
        { h: "1. Que sont les cookies ?", blocks: [
          P("Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez des sites web. Ils aident les sites à mémoriser vos préférences et à améliorer votre expérience."),
        ] },
        { h: "2. Comment nous utilisons les cookies", blocks: [
          P("EReMS Cameroon utilise des cookies aux fins suivantes :"),
          H("2.1 Cookies essentiels (obligatoires)"),
          P("Ces cookies sont nécessaires au fonctionnement de la plateforme :"),
          UL([
            "**Authentification :** vous maintenir connecté de manière sécurisée",
            "**Sécurité :** protéger contre les accès non autorisés",
            "**Gestion des sessions :** mémoriser vos actions pendant une session",
            "**Répartition de charge :** répartir le trafic entre les serveurs",
          ]),
          H("2.2 Cookies fonctionnels"),
          P("Ces cookies améliorent les fonctionnalités :"),
          UL([
            "**Préférences de langue :** mémoriser votre choix de langue",
            "**Paramètres du thème :** mémoriser votre préférence entre mode sombre et mode clair",
            "**Données de formulaire :** enregistrer temporairement les données saisies",
            "**Préférences de l'utilisateur :** mémoriser vos paramètres personnalisés",
          ]),
          H("2.3 Cookies d'analyse"),
          P("Ces cookies nous aident à comprendre l'utilisation du service :"),
          UL([
            "**Statistiques d'utilisation :** pages consultées, fonctionnalités utilisées",
            "**Performance :** temps de chargement, suivi des erreurs",
            "**Parcours de l'utilisateur :** habitudes de navigation",
            "**Informations sur l'appareil :** navigateur, système d'exploitation, taille de l'écran",
          ]),
        ] },
        { h: "3. Types de cookies que nous utilisons", blocks: [
          H("3.1 Cookies propriétaires"),
          P("Déposés directement par EReMS Cameroon :"),
          BOX([
            "**erems_token :** jeton d'authentification (essentiel)",
            "**erems_lang :** préférence de langue (fonctionnel)",
            "**erems_theme :** préférence de thème (fonctionnel)",
            "**erems_session :** identifiant de session (essentiel)",
          ]),
          H("3.2 Cookies tiers"),
          P("Déposés par des services externes que nous utilisons :"),
          UL([
            "**Stripe :** traitement des paiements (stripe.com)",
            "**Analyses :** suivi de l'utilisation (le cas échéant)",
          ]),
        ] },
        { h: "4. Durée de conservation des cookies", blocks: [
          H("Cookies de session"),
          P("Supprimés à la fermeture de votre navigateur :"),
          UL(["Identifiant de session", "Données de formulaire temporaires"]),
          H("Cookies persistants"),
          P("Conservés sur votre appareil pendant une durée déterminée :"),
          UL([
            "**Authentification :** 30 jours (ou jusqu'à la déconnexion)",
            "**Préférences :** 1 an",
            "**Analyses :** 2 ans",
          ]),
        ] },
        { h: "5. Gestion des cookies", blocks: [
          H("5.1 Paramètres du navigateur"),
          P("Vous pouvez contrôler les cookies via les paramètres de votre navigateur :"),
          UL([
            "**Chrome :** Paramètres → Confidentialité et sécurité → Cookies",
            "**Firefox :** Paramètres → Vie privée et sécurité → Cookies",
            "**Safari :** Préférences → Confidentialité → Cookies",
            "**Edge :** Paramètres → Confidentialité → Cookies",
          ]),
          H("5.2 Préférences relatives aux cookies"),
          P("Vous pouvez gérer vos préférences en matière de cookies dans les paramètres de votre compte. Veuillez noter que le blocage des cookies essentiels empêchera la plateforme de fonctionner correctement."),
          H("5.3 Ne pas suivre (Do Not Track)"),
          P("Certains navigateurs proposent une fonction « Ne pas suivre ». Nous respectons ces paramètres pour les cookies non essentiels."),
        ] },
        { h: "6. Conséquences du blocage des cookies", blocks: [
          P("Si vous bloquez les cookies, certaines fonctionnalités risquent de ne pas fonctionner :"),
          UL([
            "Vous devrez vous connecter à chaque visite",
            "Vos préférences ne seront pas enregistrées",
            "Certaines fonctionnalités pourraient être indisponibles",
            "Le site pourrait ne pas fonctionner de manière optimale",
          ]),
        ] },
        { h: "7. Mises à jour de la Politique relative aux cookies", blocks: [
          P("Nous pouvons mettre à jour la présente Politique relative aux cookies de temps à autre. Nous vous informerons des changements importants et mettrons à jour la date de « Dernière mise à jour »."),
        ] },
        { h: "8. Nous contacter", blocks: [
          P("Des questions sur les cookies ? Contactez-nous :"),
          BOX(["**Exploitant :** M-TECH", "**E-mail :** mboatechnologyllc@gmail.com"]),
        ] },
      ],
    },
  },
};

export default cookiePolicy;
